import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from '../components/rn';
import PrimaryActionButton from '../components/PrimaryActionButton';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { requestForegroundLocationPermission } from '../media/captureLocation';
import { useTranslation } from '../i18n/I18nContext';
import { fontSize, spacing, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';

type Props = {
  onContinue: () => void;
};

export default function LocationConsentScreen({ onContinue }: Props) {
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [requesting, setRequesting] = useState(false);

  async function allowLocation() {
    if (requesting) return;
    setRequesting(true);
    try {
      // Bu adım yalnız işletim sistemi iznini ister; koordinat okuması ancak
      // daha sonra, başarılı bir doğrudan kamera çekiminin ardından yapılır.
      await requestForegroundLocationPermission();
    } finally {
      setRequesting(false);
      onContinue();
    }
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={[styles.scrollContent, {
        paddingTop: insets.top + spacing.xl * 2,
        paddingBottom: insets.bottom + spacing.lg,
      }]}
      showsVerticalScrollIndicator={false}>
      <View style={styles.content}>
        <View style={styles.iconCircle}>
          <Ionicons name="location-outline" size={34} color={colors.black} />
        </View>
        <Text style={styles.title}>{t('locationConsent.title')}</Text>
        <Text style={styles.body}>{t('locationConsent.body')}</Text>
        <Text style={styles.privacy}>{t('locationConsent.privacy')}</Text>
      </View>

      <View style={styles.actions}>
        <PrimaryActionButton
          testID="onboarding-location-allow"
          label={t('locationConsent.allow')}
          disabled={requesting}
          loading={requesting}
          onPress={allowLocation}
        />
        <TouchableOpacity
          style={styles.secondaryButton}
          disabled={requesting}
          accessibilityRole="button"
          accessibilityLabel={t('locationConsent.later')}
          accessibilityState={{ disabled: requesting }}
          onPress={onContinue}>
          <Text style={styles.secondaryText}>{t('locationConsent.later')}</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: spacing.lg,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    paddingBottom: spacing.xl,
  },
  iconCircle: {
    width: 68,
    height: 68,
    borderRadius: 34,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.gray100,
    marginBottom: spacing.xl,
  },
  title: {
    color: colors.black,
    fontSize: fontSize.xl,
    fontWeight: '700',
    marginBottom: spacing.md,
  },
  body: {
    color: colors.black,
    fontSize: fontSize.md,
    lineHeight: 24,
    marginBottom: spacing.md,
  },
  privacy: {
    color: colors.gray600,
    fontSize: fontSize.sm,
    lineHeight: 21,
  },
  actions: {
    gap: spacing.sm,
  },
  secondaryButton: {
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryText: {
    color: colors.gray600,
    fontSize: fontSize.md,
  },
});
